import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Logo } from '@/components/Logo';

const PENDING_CODE = 'sportara-pending-team-code';
export default function JoinPage() {
  const { code: routeCode } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [code, setCode] = useState((routeCode || sessionStorage.getItem(PENDING_CODE) || '').trim().toUpperCase());
  const [team, setTeam] = useState<{ team_name: string; coach_name: string; sport?: string } | null>(null);
  const [child, setChild] = useState<{ player_user_id: string; display_name: string } | null>(null);
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => { if (routeCode) setCode(routeCode.trim().toUpperCase()); }, [routeCode]);
  useEffect(() => {
    if (!user || loading) return;
    let live = true;
    Promise.all([
      supabase.from('profiles').select('primary_role').eq('user_id', user.id).maybeSingle(),
      (supabase.from('player_profiles' as any) as any).select('player_user_id, display_name').eq('parent_user_id', user.id).limit(1).maybeSingle(),
    ]).then(([r, c]: any[]) => { if (live) { setRole(r.data?.primary_role || 'player'); setChild(c.data || null); } });
    return () => { live = false; };
  }, [user, loading]);
  const autoJoinTried = useRef(false);
  useEffect(() => {
    if (loading || code.length < 4) return;
    let live = true;
    supabase.rpc('preview_team_by_code', { p_code: code.toLowerCase() }).then(({ data, error: lookupError }) => {
      if (!live) return;
      setTeam(data?.[0] || null);
      setError(lookupError ? 'We could not check that code. Please try again.' : data?.length ? '' : 'This team code was not found. Check the link with your coach.');
    });
    return () => { live = false; };
  }, [code, loading]);

  const continueWithCode = () => {
    if (!/^[a-z0-9]{4,20}$/i.test(code)) { setError('Enter the team code from your coach.'); return; }
    setError(''); sessionStorage.setItem(PENDING_CODE, code);
    navigate(`/join/${encodeURIComponent(code)}`);
  };
  const join = async () => {
    if (!team || !user || busy) return;
    setBusy(true); setError('');
    const result = role === 'parent' && child
      ? await supabase.rpc('parent_join_child_to_team' as any, { p_child_user_id: child.player_user_id, p_code: code.toLowerCase() })
      : await supabase.rpc('join_team_by_code', { p_join_code: code.toLowerCase() });
    setBusy(false);
    if (result.error || (result.data as any)?.error) setError(result.error?.message || (result.data as any)?.error || 'Could not join this team. Please try again.');
    else { setDone(true); sessionStorage.removeItem(PENDING_CODE); }
  };
  // A player who signed up from this link lands back here — finish the join for them.
  useEffect(() => {
    if (autoJoinTried.current || !team || !user || !role || role === 'parent' || role === 'coach' || done) return;
    if ((sessionStorage.getItem(PENDING_CODE) || '').toUpperCase() !== code) return;
    autoJoinTried.current = true;
    join();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, user, role, code, done]);
  const startSignup = (path: 'player' | 'parent') => {
    sessionStorage.setItem(PENDING_CODE, code);
    navigate(path === 'parent' ? `/parent/signup?team=${encodeURIComponent(code)}` : '/auth/player');
  };
  return <main className="min-h-screen premium-page-bg px-4 py-10">
    <div className="max-w-md mx-auto space-y-5 rounded-2xl border border-border bg-card p-5 sm:p-8">
      <Link to="/" aria-label="Sportara home"><Logo className="w-12 h-12" alt="Sportara" /></Link>
      <h1 className="text-3xl font-display font-bold">Join your coach's team</h1>
      <p className="text-sm text-muted-foreground">No app install needed. Parents: create your account, then add your player. You must be 13+ to create an account; under 18s need parent permission.</p>
      <label htmlFor="team-code" className="block font-display font-bold text-sm">Team code</label>
      <div className="flex gap-2"><Input id="team-code" autoCapitalize="characters" value={code} maxLength={20} onChange={e => { setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')); setError(''); setTeam(null); }} placeholder="ABC123" className="font-mono uppercase h-12" /><Button className="h-12" onClick={continueWithCode}>Check code</Button></div>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {done ? <div role="status" className="space-y-3"><p className="font-bold text-primary">You're on {team?.team_name}!</p><Button onClick={() => { window.location.href = '/'; }}>Go to dashboard</Button></div> : <>
        {team && <div role="status" className="rounded-lg bg-primary/10 p-4"><p className="font-display font-bold text-lg">{team.team_name}</p><p className="text-sm text-muted-foreground capitalize">{team.sport ? `${team.sport} · ` : ''}Coach {team.coach_name}</p></div>}
        {!user ? <div className="space-y-3"><p className="text-sm">Create your free account and you'll be added to this team in the same flow — no code to type.</p><Button className="w-full h-12" disabled={!team} onClick={() => startSignup('player')}>I'm a player — join this team</Button><Button variant="outline" className="w-full h-12" disabled={!team} onClick={() => startSignup('parent')}>I'm a parent — set up my child</Button><Link className="block text-center text-sm underline" to="/auth">Already have an account? Sign in</Link></div>
        : role === 'parent' && !child ? <div className="space-y-2"><p className="text-sm">Add your player to your parent account first. Your team code will be saved.</p><Button className="w-full" onClick={() => { sessionStorage.setItem(PENDING_CODE, code); navigate('/'); }}>Add your player</Button></div>
        : role === 'coach' ? <p className="text-sm">Coaches cannot join a team as players. Use a player or parent account.</p>
        : <div className="space-y-2"><p className="text-sm">{role === 'parent' ? `Joining for ${child?.display_name || 'your player'}.` : 'Joining with your player account.'}</p><Button className="w-full" disabled={!team || busy} onClick={join}>{busy ? 'Joining…' : 'Join this team'}</Button></div>}
      </>}
      <Link to="/contact" className="block text-sm underline text-muted-foreground">Need help? Contact us</Link>
    </div>
  </main>;
}
